import { contains } from "ramda";
import { User } from "src/login/types";
import {
  BriefSpread,
  BriefSpreadStatus,
  HUMAN_READABLE_FIXED_STATUSES,
  SPREAD_STATUSES
} from "../documentList/types";

const NON_MANAGED_STATUS_TEXT = {
  [SPREAD_STATUSES.UPLOADING]: "Uploading...",
  [SPREAD_STATUSES.PROCESSING]: "Processing...",
  [SPREAD_STATUSES.PENDING_REVIEW]: "In Review",
  [SPREAD_STATUSES.PROCESSING_FAILED]: "Upload Failed",
  [SPREAD_STATUSES.COMPLETED]: "Completed",
  [SPREAD_STATUSES.REVIEW_COMPLETED]: "Completed"
};

const MANAGED_STATUS_TEXT = Object.assign({}, NON_MANAGED_STATUS_TEXT, {
  [SPREAD_STATUSES.PENDING_REVIEW]: "Ready for Review",
  [SPREAD_STATUSES.PENDING_FINAL_REVIEW]: "Ready for Final Review",
  [SPREAD_STATUSES.FINAL_REVIEW_COMPLETED]: "Completed"
});

const DISABLED_STATES = [
  SPREAD_STATUSES.UPLOADING,
  SPREAD_STATUSES.PROCESSING,
  SPREAD_STATUSES.PROCESSING_FAILED,
  SPREAD_STATUSES.REJECTED
];

const HUMAN_READABLE_FAILED_STATE = HUMAN_READABLE_FIXED_STATUSES.UPLOAD_FAILED;

const HUMAN_READABLE_PROCESSING_OR_UPLOADING_STATES = [
  HUMAN_READABLE_FIXED_STATUSES.UPLOADING,
  HUMAN_READABLE_FIXED_STATUSES.PROCESSING
];

const HUMAN_READABLE_NO_UI_OUTLINE_STATES = [
  HUMAN_READABLE_FIXED_STATUSES.UPLOADING,
  HUMAN_READABLE_FIXED_STATUSES.UPLOAD_FAILED,
  HUMAN_READABLE_FIXED_STATUSES.PROCESSING
];

const HUMAN_READABLE_COMPLETED_STATE = HUMAN_READABLE_FIXED_STATUSES.COMPLETED;

const SUCCESS_STATES = [
  SPREAD_STATUSES.PENDING_REVIEW,
  SPREAD_STATUSES.PENDING_FINAL_REVIEW,
  SPREAD_STATUSES.REVIEW_COMPLETED,
  SPREAD_STATUSES.COMPLETED,
  SPREAD_STATUSES.FINAL_REVIEW_COMPLETED
];
const DELETABLE_STATES = [
  SPREAD_STATUSES.PENDING_REVIEW,
  SPREAD_STATUSES.PENDING_FINAL_REVIEW,
  SPREAD_STATUSES.REVIEW_COMPLETED,
  SPREAD_STATUSES.FINAL_REVIEW_COMPLETED,
  SPREAD_STATUSES.PROCESSING_FAILED
];
export const COMPLETED_STATES = [
  SPREAD_STATUSES.COMPLETED,
  SPREAD_STATUSES.REVIEW_COMPLETED,
  SPREAD_STATUSES.FINAL_REVIEW_COMPLETED
];

export const getStatusText = (
  hasManagedService: boolean,
  status: BriefSpreadStatus,
  isLocked: boolean,
  rejected: boolean
): string => {
  if (rejected) {
    return "Returned for Review";
  }

  if (!hasManagedService) {
    return NON_MANAGED_STATUS_TEXT[status] || status;
  }

  if (status === SPREAD_STATUSES.PENDING_REVIEW && isLocked) {
    return "In Review";
  } else if (status === SPREAD_STATUSES.PENDING_FINAL_REVIEW && isLocked) {
    return "In Final Review";
  } else {
    return MANAGED_STATUS_TEXT[status] || status;
  }
};

export const shouldHideHumanReadableStatusOutline = (
  humanReadableStatus: string
): boolean =>
  contains(humanReadableStatus, HUMAN_READABLE_NO_UI_OUTLINE_STATES);

export const isHumanReadableStatusProcessingOrUploading = (
  humanReadableStatus: string
): boolean =>
  contains(humanReadableStatus, HUMAN_READABLE_PROCESSING_OR_UPLOADING_STATES);

export const isHumanReadableStatusFailed = (
  humanReadableStatus: string
): boolean => humanReadableStatus === HUMAN_READABLE_FAILED_STATE;

export const isHumanReadableStatusCompleted = (
  humanReadableStatus: string
): boolean => humanReadableStatus === HUMAN_READABLE_COMPLETED_STATE;

export const isSuccessful = (status: string): boolean =>
  contains(status, SUCCESS_STATES);

export const isDisabledState = (status: string): boolean =>
  contains(status, DISABLED_STATES);

export const isDeletableState = (status: string): boolean =>
  contains(status, DELETABLE_STATES);

export const isCompletedState = (status: string): boolean =>
  contains(status, COMPLETED_STATES);

export const isUserOnWindowsMachine = () => {
  return navigator.platform.indexOf("Win") > -1;
};

// Unfortunate hack to make up for no auto-show/auto-hide scrollbar on windows. #164937537
export const getDocListPaddingRight = () => {
  const WINDOWS_SCROLLBAR_WIDTH = 17;
  const DOC_LIST_PADDING_RIGHT = 30;

  const paddingRight = isUserOnWindowsMachine()
    ? `${WINDOWS_SCROLLBAR_WIDTH + DOC_LIST_PADDING_RIGHT}px`
    : `${DOC_LIST_PADDING_RIGHT}px`;

  const headerPaddingRight = { paddingRight };

  return headerPaddingRight;
};

export const getHasUnreadComments = (document: BriefSpread, user: User) =>
  document.commentsReadBy != null &&
  document.commentsReadBy.length > 0 &&
  !contains(user.userId, document.commentsReadBy);



// WEBPACK FOOTER //
// ./src/documentList/helpers.ts